import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils/ui";
import { History, X } from "lucide-react";
import { DateTime } from "luxon";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { trpc } from "@/lib/trpc/client";
import { useCoach, useUserId } from "@/lib/states/session/hooks";
import { useIsMobile } from "@/hooks/use-mobile";
import type {
  MealRevisionSnapshot,
  TrainingRevisionSnapshot,
} from "@/lib/planHistory/hydrateRevision";
import { savedRevisionToAutoSelect } from "@/lib/planHistory/revisionSelection";
import {
  useVersionHistoryHost,
  useVersionHistoryPreview,
} from "./VersionHistoryHost";

const DRAFT_ID = "draft";

type ApplyOptions = { markDirty: boolean };

type RevisionMeta = {
  id: string;
  revisionNumber: number;
  createdAt: string;
  isBaseline: boolean;
  createdBy: {
    id: string;
    firstName: string;
    lastName: string;
    avatar: { path: string; url: string } | null;
  } | null;
};

type SharedProps = {
  planId: string | null | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hasChanges: boolean;
  children: ReactNode;
};

type VersionHistorySheetProps =
  | (SharedProps & {
      kind: "training";
      liveSnapshot: TrainingRevisionSnapshot;
      onApplyRevision: (
        snapshot: TrainingRevisionSnapshot,
        options: ApplyOptions,
      ) => void;
    })
  | (SharedProps & {
      kind: "meal";
      liveSnapshot: MealRevisionSnapshot;
      onApplyRevision: (
        snapshot: MealRevisionSnapshot,
        options: ApplyOptions,
      ) => void;
    });

export default function VersionHistorySheet(props: VersionHistorySheetProps) {
  return (
    <>
      {props.children}
      {props.kind === "training" ? (
        <VersionHistoryPanel
          kind="training"
          planId={props.planId}
          open={props.open}
          onOpenChange={props.onOpenChange}
          hasChanges={props.hasChanges}
          liveSnapshot={props.liveSnapshot}
          onApplyRevision={props.onApplyRevision}
        />
      ) : (
        <VersionHistoryPanel
          kind="meal"
          planId={props.planId}
          open={props.open}
          onOpenChange={props.onOpenChange}
          hasChanges={props.hasChanges}
          liveSnapshot={props.liveSnapshot}
          onApplyRevision={props.onApplyRevision}
        />
      )}
    </>
  );
}

function VersionHistoryPanel<TSnapshot>({
  kind,
  planId,
  open,
  onOpenChange,
  hasChanges,
  liveSnapshot,
  onApplyRevision,
}: {
  kind: "training" | "meal";
  planId: string | null | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hasChanges: boolean;
  liveSnapshot: TSnapshot;
  onApplyRevision: (snapshot: TSnapshot, options: ApplyOptions) => void;
}) {
  const host = useVersionHistoryHost();
  const isMobile = useIsMobile();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hasDraft, setHasDraft] = useState(false);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const utils = trpc.useUtils();
  const storedPlanId = planId && planId.length > 2 ? planId : undefined;
  const enabled = open && Boolean(storedPlanId);

  const trainingList = trpc.training.listRevisions.useQuery(
    { planId: storedPlanId ?? "" },
    { enabled: enabled && kind === "training", staleTime: 0 },
  );
  const mealList = trpc.nutrition.listRevisions.useQuery(
    { planId: storedPlanId ?? "" },
    { enabled: enabled && kind === "meal", staleTime: 0 },
  );
  const revisions: RevisionMeta[] =
    kind === "training" ? (trainingList.data ?? []) : (mealList.data ?? []);
  const isLoading =
    kind === "training" ? trainingList.isLoading : mealList.isLoading;

  const frozenLiveRef = useRef<TSnapshot | null>(null);
  const liveSnapshotPropRef = useRef(liveSnapshot);
  const previewSnapshotRef = useRef<TSnapshot | null>(null);
  const restoredRef = useRef(false);
  const onApplyRef = useRef(onApplyRevision);
  const hasDraftRef = useRef(hasDraft);
  const hasChangesRef = useRef(hasChanges);
  const userPickedRevisionRef = useRef(false);
  const listUpdatedAtOnOpenRef = useRef(0);
  const revisionList = kind === "training" ? trainingList : mealList;
  const revisionListRef = useRef(revisionList);
  revisionListRef.current = revisionList;
  const invalidateRevisionListRef = useRef(() => {});
  invalidateRevisionListRef.current = () => {
    if (!storedPlanId) return;
    void (kind === "training"
      ? utils.training.listRevisions.invalidate({ planId: storedPlanId })
      : utils.nutrition.listRevisions.invalidate({ planId: storedPlanId }));
  };
  liveSnapshotPropRef.current = liveSnapshot;
  onApplyRef.current = onApplyRevision;
  hasDraftRef.current = hasDraft;
  hasChangesRef.current = hasChanges;

  const latestSavedId = revisions[0]?.id ?? null;
  const selectedRevision = revisions.find((revision) => revision.id === selectedId);
  const isDraftSelected = selectedId === DRAFT_ID;
  const isPreviewing =
    open &&
    !isDraftSelected &&
    selectedId != null &&
    (hasDraft || selectedId !== latestSavedId);
  const previewLabel = isPreviewing
    ? selectedRevision
      ? `Version ${selectedRevision.revisionNumber}`
      : null
    : null;

  const restorePreview = useCallback(() => {
    const snapshot = previewSnapshotRef.current;
    if (!snapshot || !selectedId || selectedId === DRAFT_ID) return;
    const clearingDraft = hasDraft && selectedId === latestSavedId;
    onApplyRef.current(snapshot, { markDirty: !clearingDraft });
    restoredRef.current = true;
    previewSnapshotRef.current = null;
    onOpenChange(false);
  }, [hasDraft, latestSavedId, onOpenChange, selectedId]);

  useEffect(() => {
    if (open) {
      if (frozenLiveRef.current) return;
      frozenLiveRef.current = liveSnapshotPropRef.current;
      setHasDraft(hasChangesRef.current);
      restoredRef.current = false;
      previewSnapshotRef.current = null;
      userPickedRevisionRef.current = false;
      listUpdatedAtOnOpenRef.current = revisionListRef.current.dataUpdatedAt;
      invalidateRevisionListRef.current();
      setError(null);
      setSelectedId(hasChangesRef.current ? DRAFT_ID : null);
      return;
    }

    if (!frozenLiveRef.current) return;
    if (!restoredRef.current && previewSnapshotRef.current) {
      onApplyRef.current(frozenLiveRef.current, {
        markDirty: hasDraftRef.current,
      });
    }
    frozenLiveRef.current = null;
    previewSnapshotRef.current = null;
    restoredRef.current = false;
    setSelectedId(null);
    setHasDraft(false);
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const nextId = savedRevisionToAutoSelect({
      hasUnsavedChanges: hasChanges || hasDraft,
      userPicked: userPickedRevisionRef.current,
      isFetching: revisionList.isFetching,
      dataUpdatedAt: revisionList.dataUpdatedAt,
      dataUpdatedAtOnOpen: listUpdatedAtOnOpenRef.current,
      latestId: revisions[0]?.id ?? null,
    });
    if (!nextId) return;
    setSelectedId((current) => (current === nextId ? current : nextId));
  }, [
    hasChanges,
    hasDraft,
    open,
    revisionList.dataUpdatedAt,
    revisionList.isFetching,
    revisions,
  ]);

  useEffect(() => {
    if (!open || selectedId !== DRAFT_ID || hasChanges || !hasDraft) return;
    frozenLiveRef.current = liveSnapshotPropRef.current;
    previewSnapshotRef.current = null;
    setHasDraft(false);
    setSelectedId(latestSavedId);
  }, [hasChanges, hasDraft, latestSavedId, open, selectedId]);

  useEffect(() => {
    if (!open || isPreviewing || hasDraft || !hasChanges) return;
    frozenLiveRef.current = liveSnapshotPropRef.current;
    setHasDraft(true);
    setSelectedId(DRAFT_ID);
  }, [hasChanges, hasDraft, isPreviewing, open]);

  useEffect(() => {
    host?.setIsPreviewing(isPreviewing);
    host?.setPreviewLabel(previewLabel);
  }, [host, isPreviewing, previewLabel]);

  useEffect(() => {
    host?.registerRestore(isPreviewing ? restorePreview : null);
  }, [host, isPreviewing, restorePreview]);

  useEffect(() => {
    return () => {
      host?.setIsPreviewing(false);
      host?.setPreviewLabel(null);
      host?.registerRestore(null);
    };
  }, [host]);

  const handleSelect = useCallback(
    async (id: string) => {
      if (id === selectedId) return;
      userPickedRevisionRef.current = true;
      setError(null);

      if (id === DRAFT_ID) {
        previewSnapshotRef.current = null;
        setSelectedId(DRAFT_ID);
        if (frozenLiveRef.current) {
          onApplyRef.current(frozenLiveRef.current, { markDirty: true });
        }
        return;
      }

      if (selectedId === DRAFT_ID) {
        frozenLiveRef.current = liveSnapshotPropRef.current;
        setHasDraft(true);
      }

      setSelectedId(id);
      if (!storedPlanId) return;
      setLoadingId(id);
      try {
        const revision =
          kind === "training"
            ? await utils.training.getRevision.fetch({
                planId: storedPlanId,
                revisionId: id,
              })
            : await utils.nutrition.getRevision.fetch({
                planId: storedPlanId,
                revisionId: id,
              });
        previewSnapshotRef.current = revision.snapshot as TSnapshot;
        onApplyRef.current(revision.snapshot as TSnapshot, {
          markDirty: false,
        });
      } catch {
        setError("Could not load that version.");
      } finally {
        setLoadingId(null);
      }
    },
    [kind, selectedId, storedPlanId, utils.nutrition.getRevision, utils.training.getRevision],
  );

  const useRail = Boolean(host?.container) && !isMobile;
  const rail = (
    <VersionTimeline
      revisions={revisions}
      selectedId={selectedId}
      loadingId={loadingId}
      isLoading={isLoading}
      error={error}
      hasDraft={hasDraft}
      canRestore={isPreviewing}
      variant={useRail ? "rail" : "sheet"}
      onSelect={handleSelect}
      onRestore={restorePreview}
      onClose={() => onOpenChange(false)}
    />
  );

  if (!open) return null;

  if (useRail && host?.container) {
    return createPortal(rail, host.container);
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
        {rail}
      </SheetContent>
    </Sheet>
  );
}

export function PlanHistoryButton({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled?: boolean;
}) {
  const host = useVersionHistoryHost();
  const isPreviewing = useVersionHistoryPreview();

  return (
    <Button
      type="button"
      variant="outline"
      disabled={disabled && !isPreviewing}
      onClick={() => {
        if (host) {
          host.setOpen((open) => !open);
          return;
        }
        onClick();
      }}
    >
      <History className="mr-1 h-4 w-4" />
      History
    </Button>
  );
}

export function PlanVersionTag() {
  const label = useVersionHistoryHost()?.previewLabel;
  const isPreviewing = useVersionHistoryPreview();
  if (!isPreviewing || !label) return null;

  return (
    <Badge variant="secondary" className="shrink-0 font-medium">
      {label}
    </Badge>
  );
}

function VersionTimeline({
  revisions,
  selectedId,
  loadingId,
  isLoading,
  error,
  hasDraft,
  canRestore,
  variant,
  onSelect,
  onRestore,
  onClose,
}: {
  revisions: RevisionMeta[];
  selectedId: string | null;
  loadingId: string | null;
  isLoading: boolean;
  error: string | null;
  hasDraft: boolean;
  canRestore: boolean;
  variant: "rail" | "sheet";
  onSelect: (id: string) => void;
  onRestore: () => void;
  onClose: () => void;
}) {
  const coach = useCoach();
  const userId = useUserId();
  const fallbackName =
    [coach?.firstName, coach?.lastName].filter(Boolean).join(" ") || "You";

  const items = useMemo(() => {
    const rows: Array<{
      id: string;
      title: string;
      description: string | null;
      timestamp: string | null;
      author: string;
      initials: string;
      avatarUrl: string | null;
      avatarTone: "primary" | "muted";
      badge: "current" | "live" | null;
    }> = [];
    const coachAvatar = coach?.avatarUrl ?? null;
    const coachInitials = initialsFromName(fallbackName);

    if (hasDraft) {
      rows.push({
        id: DRAFT_ID,
        title: "Draft",
        description: "Unsaved changes",
        timestamp: null,
        author: fallbackName,
        initials: coachInitials,
        avatarUrl: coachAvatar,
        avatarTone: "primary",
        badge: "current",
      });
    }

    revisions.forEach((revision, index) => {
      const author = authorLabel(revision.createdBy, fallbackName);
      rows.push({
        id: revision.id,
        title: `Version ${revision.revisionNumber}`,
        description: null,
        timestamp: formatWhen(revision.createdAt),
        author,
        initials: initialsFromName(author),
        avatarUrl:
          revision.createdBy?.avatar?.url ??
          (!revision.createdBy || revision.createdBy.id === userId
            ? coachAvatar
            : null),
        avatarTone: "primary",
        badge:
          !hasDraft && index === 0
            ? "current"
            : hasDraft && index === 0
              ? "live"
              : null,
      });
    });

    return rows;
  }, [coach?.avatarUrl, fallbackName, hasDraft, revisions, userId]);

  return (
    <div
      className={cn(
        "flex h-full min-h-0 flex-col bg-background text-foreground",
        variant === "rail" ? "w-80 shrink-0 border-l border-border" : "w-full",
      )}
    >
      <div className="flex items-start justify-between gap-3 border-b px-5 py-4">
        <div>
          <p className="flex items-center gap-2 text-sm font-semibold">
            <History className="h-4 w-4" />
            Version history
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {hasDraft
              ? "Your draft is selected. Choose a saved version to preview it."
              : "Select a previous version to preview it."}
          </p>
        </div>
        {variant === "rail" ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close version history</span>
          </Button>
        ) : null}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
        {isLoading && revisions.length === 0 ? (
          <p className="px-2 py-6 text-center text-sm text-muted-foreground">
            Loading versions…
          </p>
        ) : items.length === 0 ? (
          <p className="px-2 py-6 text-center text-sm text-muted-foreground">
            No saved versions yet. Save this plan to start history.
          </p>
        ) : (
          <div className="relative">
            {items.length > 1 ? (
              <span className="pointer-events-none absolute bottom-5 left-[11px] top-4 z-10 w-0.5 bg-border" />
            ) : null}
            <div className="flex flex-col">
              {items.map((item) => {
                const selected = item.id === selectedId;
                const loading = item.id === loadingId;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => onSelect(item.id)}
                    className="relative flex w-full items-start gap-3 px-1 py-1.5 text-left"
                  >
                    <span className="relative z-20 mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
                      <span
                        className={cn(
                          "rounded-full",
                          selected
                            ? "h-3 w-3 bg-primary"
                            : "h-2 w-2 bg-muted-foreground/35",
                        )}
                      />
                    </span>
                    <span
                      className={cn(
                        "min-w-0 flex-1 rounded-xl px-2 py-2 transition-colors",
                        selected ? "bg-primary/5" : "hover:bg-muted/40",
                      )}
                    >
                      <span className="flex items-center gap-2">
                        <span className="text-sm font-semibold leading-5 text-foreground">
                          {item.title}
                        </span>
                        {item.badge === "current" ? (
                          <Badge className="px-1.5 py-0 text-[10px] leading-4">
                            Current
                          </Badge>
                        ) : null}
                        {item.badge === "live" ? (
                          <Badge
                            variant="outline"
                            className="px-1.5 py-0 text-[10px] leading-4 text-muted-foreground"
                          >
                            Live
                          </Badge>
                        ) : null}
                      </span>
                      {item.description ? (
                        <span className="mt-0.5 block text-xs leading-4 text-muted-foreground">
                          {item.description}
                        </span>
                      ) : null}
                      {loading || item.timestamp ? (
                        <span className="mt-0.5 block text-xs leading-4 text-muted-foreground">
                          {loading ? "Loading…" : item.timestamp}
                        </span>
                      ) : null}
                      <span className="mt-1.5 flex items-center gap-2">
                        <Avatar className="h-5 w-5 shrink-0">
                          {item.avatarUrl ? (
                            <AvatarImage src={item.avatarUrl} alt={item.author} />
                          ) : null}
                          <AvatarFallback
                            className={cn(
                              "text-[9px] font-semibold text-white",
                              item.avatarTone === "muted"
                                ? "bg-muted-foreground/70"
                                : "bg-primary",
                            )}
                          >
                            {item.initials}
                          </AvatarFallback>
                        </Avatar>
                        <span className="truncate text-sm text-foreground/80">
                          {item.author}
                        </span>
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {error ? (
          <p className="px-2 pt-3 text-center text-sm text-destructive">{error}</p>
        ) : null}
      </div>
      <div className="border-t px-5 py-4">
        <Button
          type="button"
          className="w-full"
          disabled={!canRestore}
          onClick={onRestore}
        >
          Restore to this version
        </Button>
        <p className="mt-2 text-center text-xs text-muted-foreground">
          Does not apply until saved
        </p>
      </div>
    </div>
  );
}

function authorLabel(
  createdBy: RevisionMeta["createdBy"],
  fallback: string,
) {
  if (!createdBy) return fallback;
  return (
    [createdBy.firstName, createdBy.lastName].filter(Boolean).join(" ") ||
    fallback
  );
}

function initialsFromName(name: string) {
  const parts = name.split(" ").filter(Boolean);
  return (
    `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? parts[0]?.[1] ?? ""}`.toUpperCase() ||
    "?"
  );
}

function formatWhen(iso: string) {
  const relative = DateTime.fromISO(iso).toRelative();
  return relative ?? iso;
}
